import { Button, Card, Empty, Modal, Select, Space, Tag, Typography } from "antd";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";
import {
  getRuntimeGraphSummary,
  getSandboxPropTypeLabel,
  getSandboxRarityLabel,
} from "./runtimeBridge";
import {
  getSandboxRarityBackground,
  getSandboxRarityBorder,
  PropImage,
} from "./visuals";
import { PropPreview } from "./PropPreview";

const { Text } = Typography;

export interface ChoiceModalProps {
  open: boolean;
  title: string;
  options: PropRuntimeGraph[];
  catalog?: PropRuntimeGraph[];
  onChoose: (graph: PropRuntimeGraph) => void;
  onCancel: () => void;
  allowSkip?: boolean;
  skipText?: string;
  onSkip?: () => void;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  forceRefreshOptions?: PropRuntimeGraph[];
  forceRefreshValue?: string;
  onForceRefreshChange?: (graphId?: string) => void;
}

export function ChoiceModal({
  open,
  title,
  options,
  catalog,
  onChoose,
  onCancel,
  allowSkip,
  skipText = "跳过",
  onSkip,
  onRefresh,
  refreshDisabled,
  forceRefreshOptions = [],
  forceRefreshValue,
  onForceRefreshChange,
}: ChoiceModalProps) {
  const footer = (
    <Space style={{ width: "100%", justifyContent: allowSkip ? "space-between" : "flex-end" }}>
      <Space size={8}>
        <Text style={{ color: "#a7b4c8", fontSize: 12 }}>指定</Text>
        <Select
          allowClear
          showSearch
          size="small"
          value={forceRefreshValue}
          placeholder="不指定"
          optionFilterProp="label"
          options={forceRefreshOptions.map((graph) => ({
            value: graph._id,
            label: `${graph.name || graph._id} / ${getSandboxRarityLabel(graph.rarity)}`,
          }))}
          style={{ width: 220 }}
          popupMatchSelectWidth={320}
          onChange={(value) => onForceRefreshChange?.(value || undefined)}
        />
        <Button
          disabled={refreshDisabled || !onRefresh}
          onClick={onRefresh}
          style={{
            color: "#d8e2ef",
            background: "#1b2432",
            borderColor: "#34445f",
          }}
        >
          刷新
        </Button>
      </Space>
      {allowSkip ? (
        <button
          type="button"
          onClick={onSkip}
          style={{
            color: "#d8e2ef",
            background: "#1b2432",
            border: "1px solid #34445f",
            borderRadius: 6,
            padding: "5px 14px",
            cursor: "pointer",
          }}
        >
          {skipText}
        </button>
      ) : null}
    </Space>
  );

  return (
    <Modal
      open={open}
      title={<span style={{ color: "#f1f5f9" }}>{title}</span>}
      footer={footer}
      width={780}
      centered
      closable={Boolean(allowSkip)}
      maskClosable={Boolean(allowSkip)}
      destroyOnHidden
      styles={{
        content: {
          color: "#d8e2ef",
          background: "#101827",
          border: "1px solid #26364f",
        },
        header: {
          background: "#101827",
          borderBottom: "1px solid #26364f",
        },
        body: {
          background: "#101827",
        },
        mask: {
          background: "rgba(3, 7, 18, .72)",
        },
      }}
      onCancel={onCancel}
    >
      {options.length ? (
        <Space size={12} style={{ width: "100%" }}>
          {options.map((graph) => (
            <PropPreview key={graph._id} graph={graph} catalog={catalog}>
              <Card
                hoverable
                size="small"
                style={{
                  width: 238,
                  background: getSandboxRarityBackground(graph.rarity),
                  borderColor: getSandboxRarityBorder(graph.rarity),
                }}
                onClick={() => onChoose(graph)}
              >
                <Space direction="vertical" size={6} style={{ width: "100%" }}>
                  <PropImage graph={graph} size={52} />
                  <Text strong ellipsis style={{ color: "#f1f5f9" }}>{graph.name || graph._id}</Text>
                  <Space size={4} wrap>
                    <Tag color="blue">{getSandboxPropTypeLabel(graph.type)}</Tag>
                    <Tag color="gold">{getSandboxRarityLabel(graph.rarity)}</Tag>
                  </Space>
                  <Text style={{ color: "#a7b4c8", fontSize: 12 }}>
                    {graph.assetId || "-"}
                  </Text>
                  <Text style={{ color: "#a7b4c8", fontSize: 12 }}>
                    {getRuntimeGraphSummary(graph)}
                  </Text>
                </Space>
              </Card>
            </PropPreview>
          ))}
        </Space>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可选道具" />
      )}
    </Modal>
  );
}
